import { Button } from '@cian/ui-kit';
import * as React from 'react';
import { useSelector } from 'react-redux';
import { useDynamicPhoneFlow } from 'shared/hooks/useDynamicPhoneFlow';
import { useOfferDynamicCalltrackingEnabled } from 'shared/hooks/useOfferDynamicCalltrackingEnabled';
import { useRenderContactButtons } from 'shared/hooks/useRenderContactButtons';
import { v4 as uuid } from 'uuid';

import { trackShowPhoneClick } from './tracking';
import { DynamicCalltrackingButton } from '../../components/DynamicCalltrackingButton';
import { Phones } from '../../components/Phones';
import { getAbCallButtonText, getActualPhones, getPhones, offerSelector, selectOfferAgent } from '../../selectors';
import { getDynamicCalltrackingSiteBlockId } from '../../selectors/getDynamicCalltrackingSiteBlockId';
import { formatNumber, getPhoneLink } from '../../utils/phone';
import { trackShowStatNumber } from '../../utils/tracking/trackShowStatNumber';
import { CalltrackingBadgeContainer } from '../CalltrackingBadge';

interface IContactsButtonContainerProps {
  placeType: string;
}

export function ContactsButtonContainer({ placeType }: IContactsButtonContainerProps) {
  const [isOpenPhones, setOpenPhones] = React.useState(false);

  const siteBlockId = useSelector(getDynamicCalltrackingSiteBlockId);
  const buttonText = useSelector(getAbCallButtonText);
  const offer = useSelector(offerSelector);
  const phones = useSelector(getPhones);
  const actualPhones = useSelector(getActualPhones);
  const agent = useSelector(selectOfferAgent);

  const isOfferDynamicCalltrackingEnabled = useOfferDynamicCalltrackingEnabled();
  const { requestPhone } = useDynamicPhoneFlow();
  const [isDynamicPhoneLoading, setDynamicPhoneLoading] = React.useState(false);

  const { showPhoneButton } = useRenderContactButtons();

  const formattedPhones = React.useMemo(
    () =>
      actualPhones.map(phone => ({
        number: formatNumber(phone),
        link: getPhoneLink(phone),
      })),
    [actualPhones],
  );

  const handleOpenPhone = React.useCallback(async () => {
    // ДинКТ вторички (SEL-998)
    const clickId = isOfferDynamicCalltrackingEnabled ? uuid() : undefined;

    trackShowPhoneClick(placeType, offer, agent, clickId);

    if (clickId) {
      setDynamicPhoneLoading(true);
      const hasPhoneToShow = await requestPhone({
        offerId: offer.id,
        clickId,
        hasStaticPhones: phones.length > 0,
        onDegradation: () => trackShowStatNumber({ offer, agent, user: null, clickId }),
      });
      setDynamicPhoneLoading(false);

      if (!hasPhoneToShow) {
        return;
      }
    }

    setOpenPhones(true);
  }, [agent, offer, phones, placeType, isOfferDynamicCalltrackingEnabled, requestPhone]);

  if (!phones.length || !showPhoneButton) {
    return null;
  }

  return (
    <div>
      {siteBlockId ? (
        <DynamicCalltrackingButton
          onClick={handleOpenPhone}
          isOpen={isOpenPhones}
          buttonText={buttonText}
          blockId={siteBlockId}
          placeType={placeType}
          announcementId={offer.id}
          phone={formattedPhones[0].number}
        />
      ) : isOpenPhones ? (
        <noindex>
          <Phones phones={formattedPhones} />
        </noindex>
      ) : (
        <Button onClick={handleOpenPhone} theme="fill_primary" size="M" fullWidth loading={isDynamicPhoneLoading}>
          {buttonText}
        </Button>
      )}
      {isOpenPhones && <CalltrackingBadgeContainer />}
    </div>
  );
}
